import { Dispatch, SetStateAction, useState } from "react";
import { View, Text, TouchableOpacity, TextInput } from "react-native";
import { Feather } from "@expo/vector-icons";
import styles from "./styles";
import { useDispatch } from "react-redux";
import { login, register } from "../../../redux/slices/authSlice";
import { AppDispatch } from "../../../redux/store";
import { USE_LOCAL_DEMO_AUTH } from "../../../../firebaseConfig";
import { DEMO_EMAIL, DEMO_PASSWORD } from "../../../demo/localDemo";

function readableAuthError(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return fallback;
}

export interface AuthDetailsProps {
  authPage: 0 | 1;
  setAuthPage: Dispatch<SetStateAction<0 | 1>>;
  setMenuMessage: Dispatch<SetStateAction<string>>;
  setDetailsPage: Dispatch<SetStateAction<boolean>>;
}

/**
 * Function that renders a component that renders a signin/signup
 * form.
 *
 * @param props passed to component
 * @param props.authPage if 0 it is in the signin state
 * if 1 is in the signup state
 * @param props.setDetailsPage setter for the variable that chooses
 * the type of page, if true show AuthMenu else show AuthDetails
 * @returns Component
 */
export default function AuthDetails({
  authPage,
  setAuthPage,
  setMenuMessage,
  setDetailsPage,
}: AuthDetailsProps) {
  const [email, setEmail] = useState(USE_LOCAL_DEMO_AUTH ? DEMO_EMAIL : "");
  const [password, setPassword] = useState(
    USE_LOCAL_DEMO_AUTH ? DEMO_PASSWORD : "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch: AppDispatch = useDispatch();

  const handleLogin = async () => {
    if (!email.trim() || !password) {
      setErrorMessage("请输入登录邮箱和密码");
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      await dispatch(login({ email: email.trim(), password })).unwrap();
    } catch (error) {
      setErrorMessage(readableAuthError(error, "登录失败，请检查邮箱和密码"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async () => {
    if (!email.trim() || !password) {
      setErrorMessage("请输入注册邮箱和密码");
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      await dispatch(register({ email: email.trim(), password })).unwrap();
      setDetailsPage(false);
      setAuthPage(1);
      setMenuMessage("账号创建成功");
    } catch (error) {
      setErrorMessage(readableAuthError(error, "注册失败，请稍后重试"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={() => setDetailsPage(false)}>
        <Feather name="arrow-left" size={24} color="black" />
      </TouchableOpacity>
      <TextInput
        value={email}
        onChangeText={(text) => setEmail(text)}
        style={styles.textInput}
        placeholder="Email"
        autoCapitalize="none"
        keyboardType="email-address"
      />
      <TextInput
        value={password}
        onChangeText={(text) => setPassword(text)}
        style={styles.textInput}
        secureTextEntry
        placeholder="Password"
      />

      {errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null}

      <TouchableOpacity
        disabled={submitting}
        style={[styles.button, submitting && styles.buttonDisabled]}
        onPress={() =>
          void (authPage == 0 ? handleLogin() : handleRegister())
        }
      >
        <Text style={styles.buttonText}>
          {submitting
            ? authPage == 0
              ? "正在登录…"
              : "正在注册…"
            : authPage == 0
            ? "登录"
            : "注册"}
        </Text>
      </TouchableOpacity>
    </View>
  );
}
